import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import usePrompt from 'hooks/usePrompt';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IAdminStore,
  IUserOrganization,
  navStore,
  STORE_LOAD_FRESH,
  useStore,
} from '@mcn-platform/models';

import TagsInput from 'components/forms/TagsInput/TagsInput';
import EditorBreadcrumbs from 'components/editor/EditorBreadcrumbs';

const UserEditor = () => {
  const store = useStore() as IAdminStore;
  const [userOrganization, setUserOrganization] = useState<IUserOrganization>();
  const [email, setEmail] = useState<string>('');

  const { userOrgID } = useParams();
  const navigate = useNavigate();

  const isSuperUser = store.currentUserRoles.includes('SUPERUSER');

  usePrompt({
    isDirty: userOrganization
      ? (userOrganization.changeCount > 0 || (userOrganization.user?.changeCount ?? 0) > 0)
      : false,
  });

  useEffect(() => {
    if (!userOrgID) {
      return;
    }

    // check if we're creating a new userOrganization
    if (userOrgID === 'new') {
      // don't need to
    } else {
      const stub = store.getOrLoad('userOrganization', userOrgID, STORE_LOAD_FRESH);
      setUserOrganization(stub);
    }
    // Do not depend on syncState — getOrLoad(STORE_LOAD_FRESH) would loop when load completes.
  }, [store, userOrgID]);

  /**
   * Takes the full userOrganization and saves it to the userOrganization stub.
   */
  // const saveUserOrganization = () => {
  //   if (!userOrganization) return;

  //   if (userOrgID === 'new') {
  //     // navStore.showMessageDialog('Creating UserOrganization', 'Creating userOrganization');
  //   } else {
  //     store.update('userOrganization', userOrganization);
  //   }
  // };
  const saveUserOrganization = () => {
    if (!userOrganization) return;

    if (userOrgID === 'new') {
      store.addStub('userOrganization', userOrganization).then(() => {
        navigate(`/app/userOrg/${userOrganization.id}`);
      });
    } else {
      const u = userOrganization.user;
      if (u && u.changeCount > 0) {
        store.update('user', u);
      }
      store.update('userOrganization', userOrganization);
    }
  };

  const submitNew = () => {
    navStore.showMessageDialog('Inviting User', 'Creating invite...');
    const defaultRoles = store.currentOrganization?.defaultRoles || ['FULLADMIN'];
    store.addUserToOrganization(email, defaultRoles).then((newUserOrg) => {
      console.log('created new userOrg');
      navStore.hideMessageDialog();
      navStore.showConfirmDialog(
        'Success',
        'User has been invited.  If this is a new user they will receive an email with a temporary password.',
        () => navStore.hideConfirmDialog(),
        'OK',
      );
      navigate(`../userOrg/${newUserOrg.id}`);
    }).catch((reason) => {
      console.log('Error inviting user', reason);
      navStore.hideMessageDialog();
      navStore.showConfirmDialog(
        'Error',
        'There was a problem inviting the user to the organization.',
        () => navStore.hideConfirmDialog(),
        'OK',
      );
    });
    return false;
  };

  if (userOrgID === 'new') {
    return (
      <Stack direction="column" spacing={2}>
        <Helmet>
          <title>Add User to Organization</title>
        </Helmet>
        <EditorBreadcrumbs currentLabel="Invite user" />
        <FormControlLabel
          label="Email"
          labelPlacement="top"
          control={(
            <TextField
              fullWidth
              id="email"
              name="email"
              placeholder="Enter email"
              value={email}
              onChange={(event:any) => {
                console.log('setting email to new value', event);
                setEmail(event.target.value); // onChange(newValue);
              }}
            />
          )}
        />
        <Box>
          <Button variant="contained" onClick={submitNew}>Invite User</Button>
        </Box>
      </Stack>
    );
  }

  if (!userOrganization) {
    return (
      <div>Loading.</div>
    );
  }
  if (userOrganization.syncState === 'error') {
    return <div>Error loading user</div>;
  }

  if (userOrganization.syncState === 'saving') {
    return <div>Saving..</div>;
  }

  if (userOrgID !== 'new' && userOrganization.syncState !== 'loaded') {
    return <div>Loading..</div>;
  }

  return (
    <Stack direction="column" spacing={2}>
      <Helmet>
        <title>User Information</title>
      </Helmet>
      <EditorBreadcrumbs
        currentLabel={userOrganization.user.email || 'User'}
      />
      <FormControlLabel
        label="Email"
        labelPlacement="top"
        control={(
          <TextField
            fullWidth
            id="email"
            name="email"
            value={userOrganization.user.email}
            disabled
          />
        )}
      />
      <FormControlLabel
        label="Name"
        labelPlacement="top"
        control={(
          <TextField
            fullWidth
            id="name"
            name="name"
            value={userOrganization.user.name}
            disabled
          />
        )}
      />
      <FormControlLabel
        label="Admin assistant (app bar)"
        control={(
          <Checkbox
            checked={userOrganization.user?.enableAdminAgent === true}
            onChange={(_, checked) => {
              userOrganization.user?.updateValues({ enableAdminAgent: checked });
            }}
            disabled={!isSuperUser || !userOrganization.user}
          />
        )}
      />
      <FormControlLabel
        label="Roles"
        labelPlacement="top"
        control={(
          <TagsInput
            id="userRoles"
            name="userRoles"
            placeholder="Select User Roles"
            options={['FULLADMIN', 'AIADMIN', 'CONTENT_APPROVER', 'CONTENT_EDITOR']}
            optionLabels={['Full Admin', 'AI Admin', 'Content Approver', 'Content Editor']}
            value={userOrganization?.roles || []}
            onChange={(tags) => {
              console.log('update to', tags);
              userOrganization.updateValues({ roles: tags });
            }}
            onBlur={() => {}}
            disabled={!isSuperUser}
            // error={isTouched && !!error}
            // helperText={meta.touched && meta.error}
            fullWidth
          />
        )}
      />
      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={saveUserOrganization}
          disabled={
            userOrganization.changeCount === 0
            && (userOrganization.user?.changeCount ?? 0) === 0
          }
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
};

export default observer(UserEditor);
